/**
 * Regenerate the thumbnail of an existing recording, mirroring the "Generate thumbnail" button of
 * aura-controltools (auracontroltools/views/recidview.py):
 *
 *   1. take the rtsp url from the recording processing config (`provider.url`)
 *   2. ask the device for a fresh frame (GetThumbnailCommand)
 *   3. apply the grabber transformer chain (`Grabber.transformers`) so the thumbnail matches what
 *      the pipeline actually sees
 *   4. resize to the configured `provider.width` x `provider.height` and post it to the CoreAPI,
 *      reusing the same compression + patch the thumbnails widget uses when creating a recid
 */
import {device} from '/src/services/iot-api';
import {recordings} from '/src/services/api';

import {base64JpegToBlob, resizeBlob} from './thumbnails';

const _canvas = (width, height, fill = null) => {
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.trunc(width));
    canvas.height = Math.max(1, Math.trunc(height));
    if (fill) {
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = fill;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
    return canvas;
}

const _scaled = (canvas, width, height) => {
    const out = _canvas(width, height);
    out.getContext('2d').drawImage(canvas, 0, 0, out.width, out.height);
    return out;
}

/**
 * Port of auracontroltools/tools/transformers.py. Only the transformers that map onto a 2d canvas
 * are implemented; anything else (DewrapTransformer) throws so we never upload a thumbnail that
 * does not match what the device produces.
 */
const TRANSFORMERS = {
    IdentityTransformer: (canvas) => canvas,

    CropTransformer: (canvas, {p = [0, 0], q = [-1, -1]}) => {
        const sizes = [canvas.width, canvas.height];
        // values < 1 are fractions of the image size, >= 1 are pixels
        const start = sizes.map((size, i) => Math.max(0, Math.trunc(p[i] < 1 ? p[i] * size : p[i])));
        const end = sizes.map((size, i) => {
            const value = Math.trunc(q[i] < 1 ? q[i] * size : q[i]);
            return value > 1 ? Math.min(value, size) : size;
        });
        const [width, height] = [end[0] - start[0], end[1] - start[1]];
        if (width <= 0 || height <= 0)
            throw new Error(`CropTransformer p=${p} q=${q} leaves an empty image`);
        const out = _canvas(width, height);
        out.getContext('2d').drawImage(canvas, start[0], start[1], width, height, 0, 0, width, height);
        return out;
    },

    ScaleTransformer: (canvas, {scale = -1, size = [-1, -1], short = -1}) => {
        const {width, height} = canvas;
        if (typeof scale === 'number' && scale > 0)
            return _scaled(canvas, width * scale, height * scale);
        if (Array.isArray(scale) && scale.length === 2 && scale.every(s => s > 0))
            return _scaled(canvas, width * scale[1], height * scale[0]);
        // size is (height, width), values <= 1 keep the original dimension
        if (Array.isArray(size) && size.length === 2 && size.some(s => s > 1))
            return _scaled(canvas, size[1] > 1 ? size[1] : width, size[0] > 1 ? size[0] : height);
        if (typeof short === 'number' && short > 1) {
            const ratio = short / Math.min(width, height);
            return _scaled(canvas, width * ratio, height * ratio);
        }
        return canvas;
    },

    // cv2.warpAffine keeps the original frame size and leaves the uncovered corners black
    RotationTransformer: (canvas, {angle = 90, scale = 1.}) => {
        const {width, height} = canvas;
        const out = _canvas(width, height, '#000');
        const ctx = out.getContext('2d');
        ctx.translate(width / 2, height / 2);
        ctx.rotate(-angle * Math.PI / 180);  // cv2 angles are counter-clockwise
        ctx.scale(scale, scale);
        ctx.drawImage(canvas, -width / 2, -height / 2);
        return out;
    },

    ChannelInversionTransformer: (canvas) => {
        const ctx = canvas.getContext('2d');
        const image = ctx.getImageData(0, 0, canvas.width, canvas.height);
        for (let i = 0; i < image.data.length; i += 4)
            [image.data[i], image.data[i + 2]] = [image.data[i + 2], image.data[i]];
        ctx.putImageData(image, 0, 0);
        return canvas;
    },
};

// MultiTransformer.factory takes either a list of {name, params} or a {name: params} mapping
const _asTransformerList = (transformers) => {
    if (!transformers)
        return [];
    if (Array.isArray(transformers))
        return transformers.map(t => ({name: t?.name, params: t?.params ?? {}}));
    if (typeof transformers === 'object')
        return Object.entries(transformers).map(([name, params]) => ({name, params: params ?? {}}));
    return [];
}

const _blobToCanvas = (blob) => new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(blob);
    img.onload = () => {
        const canvas = _canvas(img.naturalWidth, img.naturalHeight);
        canvas.getContext('2d').drawImage(img, 0, 0);
        URL.revokeObjectURL(url);
        resolve(canvas);
    };
    img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('The device returned an image that could not be decoded'));
    };
    img.src = url;
});

// png keeps the intermediate lossless, the jpeg compression happens once in resizeBlob
const _canvasToBlob = (canvas) => new Promise((resolve, reject) => canvas.toBlob(
    blob => blob ? resolve(blob) : reject(new Error('Failed to encode the transformed thumbnail')),
    'image/png'
));

const _transformed = async (blob, transformers) => {
    const canvas = transformers.reduce((im, {name, params}) => {
        const transformer = TRANSFORMERS[name];
        if (!transformer)
            throw new Error(`Cannot apply "${name}" in the browser, use aura-controltools for this recording`);
        return transformer(im, params);
    }, await _blobToCanvas(blob));
    return _canvasToBlob(canvas);
}

/**
 * Ask `deviceName` for a frame of `recording`'s source and store it as the recording thumbnail.
 * Resolves with the uploaded {width, height}, rejects with a displayable message.
 */
export const regenerateThumbnail = async ({deviceName, recording}) => {
    const config = recording?.processingConfig;
    const provider = config?.provider;
    if (!provider?.url)
        throw new Error('The recording has no rtsp url in its processing config');

    const [width, height] = [Number(provider.width), Number(provider.height)];
    if (!width || !height)
        throw new Error('The recording has no width/height in its processing config');

    const resp = await device.command('GetThumbnailCommand', deviceName, {source: provider.url});
    const payload = resp?.payload?.resp;
    if (typeof payload !== 'string')
        throw new Error(payload?.error ?? 'The device did not return a thumbnail');

    const transformers = _asTransformerList(config?.Grabber?.transformers);
    const frame = transformers.length
        ? await _transformed(base64JpegToBlob(payload), transformers)
        : base64JpegToBlob(payload);

    // 0.4 is the quality RECID.thumbnail starts at in aura-controltools — the widget can afford 0.7
    // because it downsizes to 480px first, here we keep the full configured resolution
    const {img} = await resizeBlob(frame, height, width, 0.4);
    await recordings.thumbnail.patch(recording.id, {base64EncodedJpeg: img});
    return {width, height};
};
